/**
 * Shared hostname-normalization helpers for the landing app.
 *
 * Before GEN-2251, ~20 inline copies of `new URL(x).hostname.replace(/^www\./)`
 * were scattered across the AI-visibility checker and discovery-report UI. The
 * copies diverged in two ways that matter for correctness:
 *
 *   1. **Case handling** – some appended `.toLowerCase()`, some used `/^www\./i`,
 *      some neither. For a *valid* URL this divergence is invisible: the WHATWG
 *      URL parser already lowercases `url.hostname`, so `new URL('https://WWW.X.COM')
 *      .hostname` is `www.x.com` regardless. The case-handling difference only
 *      surfaced on the **malformed-input fallback** path (where there is no parser
 *      to do the lowercasing).
 *   2. **Error fallback** – when `new URL()` throws, copies variously returned the
 *      raw string, `null`, `''`, or a hand-parsed best-effort hostname.
 *
 * These helpers make both choices explicit so call sites keep their exact
 * observable behavior. Pick the primitive whose fallback matches the call site:
 *
 *   - {@link hostnameOf}      – comparison/extraction primitive; `null` on failure.
 *   - {@link displayHostname} – display primitive; configurable fallback (default: raw).
 *   - {@link looseHostname}   – no-parser hand-parse used inside the "rich" display
 *                               fallbacks (strip protocol + leading `www.` + path).
 *
 * For a *valid* URL all three return the same `www.`-stripped, lowercased host.
 */

/**
 * Strip the leading `www.` prefix from an already-extracted bare host string.
 *
 * Operates on a host like `www.example.com` (NOT a full URL). The match is
 * anchored so embedded `www` (e.g. `wwwbackup.example.com`) is preserved, and
 * case-insensitive so an un-normalized host still loses its prefix.
 *
 * @param host - A bare hostname (no scheme, no path).
 * @returns The host with a single leading `www.` removed.
 */
export function stripWww(host: string): string {
  return host.replace(/^www\./i, '');
}

/**
 * Extract a normalized hostname from a URL string, or `null` if it cannot be
 * parsed.
 *
 * For a valid URL the result is the lowercased host with `www.` stripped
 * (`https://WWW.Example.com/x` → `example.com`). For a malformed input it
 * returns `null` so callers can branch on "couldn't extract a host".
 *
 * Use this for hostname *comparison* and for call sites that previously caught
 * `new URL()` and returned `null`/`undefined`.
 *
 * @param raw - A URL string (may be malformed).
 * @returns The normalized hostname, or `null` on parse failure.
 */
export function hostnameOf(raw: string): string | null {
  try {
    // `url.hostname` is already lowercased by the WHATWG URL parser.
    return stripWww(new URL(raw).hostname);
  } catch {
    return null;
  }
}

/**
 * Extract a hostname for display, falling back to a caller-supplied value when
 * the input cannot be parsed.
 *
 * For a valid URL the result is the lowercased host with `www.` stripped. For a
 * malformed input it returns `fallback` (default: the raw input verbatim, which
 * matches the historical `formatHostname` behavior). Pass `fallback: ''` to get
 * an empty string instead, or any other sentinel a call site needs.
 *
 * The returned value is for display only; callers should continue to pass the
 * full original URL to an anchor's `href`.
 *
 * @param raw - A URL string (may be malformed).
 * @param fallback - Value returned when parsing fails. Defaults to `raw`.
 * @returns The normalized hostname, or `fallback` on parse failure.
 */
export function displayHostname(raw: string, fallback: string = raw): string {
  return hostnameOf(raw) ?? fallback;
}

/**
 * Best-effort hostname extraction that does NOT use the URL parser.
 *
 * Strips the scheme (`http(s)://`), a single leading `www.`, and everything from
 * the first `/` onward. This reproduces the hand-parsed fallback that several
 * display call sites used when `new URL()` threw – they preferred a degraded
 * hostname over showing the raw string. Unlike {@link hostnameOf}, this is NOT
 * lowercased, matching that historical behavior.
 *
 * @param raw - A URL-ish string that may not be a valid URL.
 * @returns A best-effort bare hostname.
 */
export function looseHostname(raw: string): string {
  return stripWww(raw.replace(/^https?:\/\//i, '')).split('/')[0];
}

/**
 * Compare two normalized hostnames for "same site" equality, treating a
 * subdomain and its parent as equal (`surgeons.hss.edu` ≍ `hss.edu`).
 *
 * Inputs must already be normalized bare hosts (e.g. from {@link hostnameOf}) –
 * this function does no lowercasing or `www.` stripping of its own.
 *
 * Originally file-private in `google-fetcher.ts` (`computeProspectRank` host
 * matching); lifted here so the render-time Google-presence derivation
 * (GEN-2283) can use the exact same host semantics without importing the
 * fetcher (which pulls in server-only PostHog reporting).
 *
 * @param a - A normalized bare hostname.
 * @param b - A normalized bare hostname.
 * @returns Whether the hosts identify the same site.
 */
export function hostsEqual(a: string, b: string): boolean {
  return a === b || a.endsWith(`.${b}`) || b.endsWith(`.${a}`);
}

/**
 * Canonicalize a website URL to a single stable "site identity" string.
 *
 * Two URLs that point at the same site but differ only by scheme, a `www.`
 * prefix, host casing, or a trailing slash collapse to ONE value:
 *
 *   canonicalizeSiteUrl('https://www.dulyhealthandcare.com/')  // 'https://dulyhealthandcare.com'
 *   canonicalizeSiteUrl('dulyhealthandcare.com')               // 'https://dulyhealthandcare.com'
 *   canonicalizeSiteUrl('HTTP://WWW.Duly.COM/')                // 'https://duly.com'
 *
 * WHY (GEN – AI-visibility-checker dedup): the discovery-report checker keys a
 * check's identity on the submitted URL (the direct-URL input path stores it as
 * `place_id`). Without canonicalization, `dulyhealthandcare.com` and
 * `www.dulyhealthandcare.com/` are two identities that each trigger an
 * independent, non-deterministic re-run of the scoring pipeline – producing two
 * contradictory scores for one site. Collapsing them to one identity lets the
 * run path reuse a recent completed result instead of re-rolling.
 *
 * Normalization applied (for a parseable URL):
 *   - force `https://` scheme (input scheme, including bare/`http`, is dropped)
 *   - lowercase host (the WHATWG parser already does this) and strip one leading `www.`
 *   - keep the path but drop trailing slash(es); drop query string and fragment
 *
 * Idempotent: `canonicalizeSiteUrl(canonicalizeSiteUrl(x)) === canonicalizeSiteUrl(x)`.
 *
 * Fallback: if the input cannot be parsed as a URL (even after prefixing
 * `https://`), the RAW input is returned unchanged – we never emit a
 * `https://`-prefixed garbage string. Callers that gate on `startsWith('http')`
 * (e.g. "is this a URL-shaped placeId?") therefore won't mis-fire on garbage.
 *
 * @param raw - A website URL (with or without scheme; may be malformed).
 * @returns The canonical `https://<host><path>` identity, or `raw` on parse failure.
 */
export function canonicalizeSiteUrl(raw: string): string {
  const trimmed = raw.trim();
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const parsed = new URL(withScheme);
    const host = stripWww(parsed.hostname); // hostname is already lowercased by the parser
    const path = parsed.pathname.replace(/\/+$/, ''); // drop trailing slash(es); '/' → ''
    return `https://${host}${path}`;
  } catch {
    return raw;
  }
}
